import { IntlMessageFormat } from 'intl-messageformat';
import { answerValues, tidyAnswer } from './answers.js';
import type { RuleField, RuleGroupView } from './schemas/rules.js';

/*
 * Общий для интерфейса и сервера MCP (005, T056: ответ агенту — тем же текстом, что на странице).
 * Ответ правила словами (004, data-model А.2; contracts/rule-groups.md, «Шаблон ответа»): шаблон ICU MessageFormat
 * с сервера и значения полей версии. Интерфейс не зависит от движка: шаблон, значения (`values`) и их тексты
 * (`texts`) приходят в `RuleGroupView`. Значение-текст (число с единицей, время, список) — часть `value`: в просмотре
 * оно жирное; ветки `select` — обычный текст.
 */

/** Поля правил по ключу `кубик.поле`: тип поля решает, идёт ли значение ключом `select`. */
export type FieldIndex = ReadonlyMap<string, RuleField>;

export function fieldIndex(groups: readonly RuleGroupView[]): FieldIndex {
  const out = new Map<string, RuleField>();
  for (const g of groups) for (const f of g.fields) out.set(`${f.cube}.${f.name}`, f);
  return out;
}

/** Кусок ответа: `at` — место в тексте (ключ для отрисовки), `value` — значение поля. */
export interface AnswerPart {
  readonly at: number;
  readonly text: string;
  readonly value?: true;
}

/** Метки значения внутри текста: символы из области частного использования, в шаблонах их нет. */
const OPEN = '';
const CLOSE = '';
const MARKED = /([^]*)/gu;

/** Значения правила по ключу `кубик.поле`: версии, черновика или введённые в изменении. */
export type Values = Readonly<Record<string, unknown>>;

const canonical = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (typeof v === 'object' && v !== null)
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonical((v as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  return JSON.stringify(v ?? null);
};

/** Значения равны без учёта порядка ключей. */
export const sameValue = (a: unknown, b: unknown): boolean => canonical(a) === canonical(b);

const fmtNum = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const DAYS_RU = new Intl.PluralRules('ru-RU');

/** Длительность в секундах словами — как тексты сервера: 10 с, 15 мин, 1 ч, 1 ч 30 мин. */
export function secondsText(s: number): string {
  if (s < 60) return `${fmtNum.format(s)} с`;
  if (s % 3600 === 0) return `${fmtNum.format(s / 3600)} ч`;
  if (s > 3600) return `${Math.floor(s / 3600)} ч ${fmtNum.format((s % 3600) / 60)} мин`;
  return `${fmtNum.format(s / 60)} мин`;
}

/** Число с единицей поля — как тексты значений сервера (`valueText` движка). */
function numberText(n: number, unit: string | undefined): string {
  switch (unit) {
    case 'сек':
      return secondsText(n);
    case 'дни': {
      const c = DAYS_RU.select(n);
      return `${fmtNum.format(n)} ${c === 'one' ? 'день' : c === 'few' ? 'дня' : 'дней'}`;
    }
    case undefined:
    case '':
      return fmtNum.format(n);
    default:
      return `${fmtNum.format(n)} ${unit}`;
  }
}

/** Пометка расчёта словами из её кода: «A1.нет_выхода» → «нет выхода». */
export function flagText(code: string): string {
  return code.replace(/^[^.]+\./u, '').replaceAll('_', ' ');
}

/**
 * Текст значения без сервера — для значений черновика и введённых в изменении: текст варианта, число с единицей,
 * «да» и «нет», список. Значение версии — текст с сервера (`texts`), он точнее («1 ч 30 мин»).
 */
export function localValueText(f: RuleField | undefined, v: unknown): string {
  if (v === null || v === undefined) return '';
  const option = (x: unknown) => f?.options?.find((o) => sameValue(o.value, x))?.doc;
  const known = option(v);
  if (known) return known;
  if (typeof v === 'boolean') return v ? 'да' : 'нет';
  if (typeof v === 'number') return numberText(v, f?.unit);
  if (Array.isArray(v))
    return v.length === 0
      ? 'нет'
      : v.map((x) => option(x) ?? (typeof x === 'string' ? flagText(x) : String(x))).join(', ');
  if (typeof v === 'string') return v;
  return '';
}

/**
 * Текст значения поля. Без `values` или при значении версии — текст с сервера (`texts`); иначе — `localValueText`.
 * Черновик сервер может прислать со своими текстами (`draft.texts`) — тогда они.
 */
export function valueText(g: RuleGroupView, key: string, values?: Values, fields?: FieldIndex): string {
  const v = values ? values[key] : g.values[key];
  if (values === undefined || sameValue(v, g.values[key])) {
    const t = g.texts[key];
    if (t !== undefined) return t;
    if (v === null || v === undefined) return '';
    if (Array.isArray(v)) return v.map(String).join(', ');
    if (typeof v === 'boolean') return v ? 'да' : 'нет';
    return String(v);
  }
  const drafted = draftTexts(g)?.[key];
  if (drafted !== undefined && sameValue(v, g.draft?.values[key])) return drafted;
  return localValueText(fields?.get(key), v);
}

/** Тексты значений черновика, если сервер их прислал (в схеме `RuleGroupView.draft` их пока нет). */
function draftTexts(g: RuleGroupView): Readonly<Record<string, string>> | undefined {
  const t = (g.draft as { texts?: unknown } | undefined)?.texts;
  return typeof t === 'object' && t !== null ? (t as Record<string, string>) : undefined;
}

/** Поле-выбор (вариант или да/нет): его значение идёт в `select` ключом (`@tabel/shared/answers`). */
export function isChoice(fields: FieldIndex, values: Values, key: string): boolean {
  const f = fields.get(key);
  if (f) return f.control === 'select' || f.control === 'boolean';
  return typeof values[key] === 'boolean';
}

/** Значения аргументов шаблона: ключи `select`, `true`/`false` списка, число элементов, тексты значений. */
export function argValues(
  g: RuleGroupView,
  fields: FieldIndex,
  values: Values,
  text: (key: string) => string,
): Record<string, string | number> {
  return answerValues(g.args, {
    get: (key) => values[key],
    isChoice: (key) => isChoice(fields, values, key),
    text,
  });
}

/** Ответ правила кусками. Неверный шаблон или нет значения — исключение `intl-messageformat`. */
export function answerParts(g: RuleGroupView, fields: FieldIndex, own?: Values): AnswerPart[] {
  const values = argValues(
    g,
    fields,
    own ?? g.values,
    (key) => `${OPEN}${valueText(g, key, own, fields)}${CLOSE}`,
  );
  const raw = String(new IntlMessageFormat(g.answer, 'ru').format(values));
  const text = tidyAnswer(tidyAnswer(raw).replace(//gu, ''));
  const parts: AnswerPart[] = [];
  let last = 0;
  for (const m of text.matchAll(MARKED)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ at: last, text: text.slice(last, at) });
    parts.push({ at, text: m[1] ?? '', value: true });
    last = at + m[0].length;
  }
  if (last < text.length) parts.push({ at: last, text: text.slice(last) });
  return parts;
}

/** Строка под ответом: «подпись: значение» (data-model А.2, `details`). */
export interface DetailLine {
  readonly key: string;
  readonly label: string;
  readonly value: string;
}

/** Текст сервера для значения, которого нет (`valueText` движка). */
const UNSET = 'не задано';

/**
 * Поля под ответом; пустое значение и «не задано» не показываются: строка «Кто ставит отметку: не задано» под ответом
 * ничего не говорит заказчику (ревью T103, С-15). `values` — значения черновика.
 */
export function detailLines(g: RuleGroupView, fields: FieldIndex, values?: Values): DetailLine[] {
  const all = values ?? g.values;
  return g.details.flatMap((key) => {
    const f = fields.get(key);
    // Настройка, которая при этих значениях не действует (`showWhen`), — не строка «Подробнее» (приёмка 16.10.2026:
    // «самый ранний вход» при «сумме отрезков»).
    if (f?.showWhen && !f.showWhen.in.includes(all[`${f.cube}.${f.showWhen.field}`])) return [];
    const value = valueText(g, key, values, fields).trim();
    const label = f?.label;
    return value && value !== UNSET && label ? [{ key, label, value }] : [];
  });
}

/** Текст ответа и строк под ним — для поиска; шаблон не отрисовался — пусто. */
export function plainAnswer(g: RuleGroupView, fields: FieldIndex, values?: Values): string {
  let answer = '';
  try {
    answer = answerParts(g, fields, values)
      .map((p) => p.text)
      .join('');
  } catch {
    // ответ не отрисовался — ищем по вопросу и строкам под ответом
  }
  return [answer, ...detailLines(g, fields, values).map((d) => `${d.label}: ${d.value}`)].join(' ');
}

// ── Шаблон ответа деревом: изменение на месте (T063) ──

/**
 * Узел разобранного шаблона ICU (`IntlMessageFormat#getAst`, `@formatjs/icu-messageformat-parser`). Виды: 0 — текст,
 * 1 — значение `{имя}`, 5 — `select`, 6 — `plural`, 7 — `#`, 8 — тег; 2–4 — число и даты (в ответах правил их нет).
 */
export type AnswerNode =
  | { readonly type: 0; readonly value: string }
  | { readonly type: 1 | 2 | 3 | 4; readonly value: string }
  | { readonly type: 5; readonly value: string; readonly options: Readonly<Record<string, AnswerBranch>> }
  | {
      readonly type: 6;
      readonly value: string;
      readonly offset?: number;
      readonly options: Readonly<Record<string, AnswerBranch>>;
    }
  | { readonly type: 7 }
  | { readonly type: 8; readonly value: string; readonly children: readonly AnswerNode[] };

export interface AnswerBranch {
  readonly value: readonly AnswerNode[];
}

/** Шаблон ответа деревом; неверный шаблон — исключение `intl-messageformat`. */
export function answerTree(answer: string): readonly AnswerNode[] {
  return new IntlMessageFormat(answer, 'ru').getAst() as unknown as readonly AnswerNode[];
}

const PLURAL_RU = new Intl.PluralRules('ru');

/** Ветка `plural` для числа: точная `=N`, затем по правилу числа, затем `other`. */
export function pluralBranch(
  node: Extract<AnswerNode, { type: 6 }>,
  n: number,
): readonly AnswerNode[] | undefined {
  const exact = node.options[`=${n}`];
  if (exact) return exact.value;
  const rule = PLURAL_RU.select(n - (node.offset ?? 0));
  return (node.options[rule] ?? node.options.other)?.value;
}

/** В ветке есть конец предложения: такие варианты идут строками, остальные — внутри предложения. */
export function hasSentence(nodes: readonly AnswerNode[] | undefined): boolean {
  return (nodes ?? []).some(
    (n) =>
      (n.type === 0 && /[.!?]/u.test(n.value)) ||
      ((n.type === 5 || n.type === 6) && Object.values(n.options).some((b) => hasSentence(b.value))),
  );
}
